import React from "react";
import {
  getWorkflowStepChecklist,
  normalizeWorkflowJson
} from "./WorkflowProtocolEditor";
import { getAuthToken } from "../services/auth";
import { getBaseUrl } from "../services/baseUrl";

type WorkflowFeedbackScope = "WORKFLOW" | "SECTION" | "ITEM";

type WorkflowFeedbackTarget = {
  scope: WorkflowFeedbackScope;
  title: string;
  stepIndex?: number;
  itemIndex?: number;
  step?: any;
  item?: any;
};

type WorkflowProtocolPreviewProps = {
  workflowJson: any;
  emptyText?: string;
  onFeedbackRequest?: (target: WorkflowFeedbackTarget) => void;
};

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle,
  placeholderText = "No preview available"
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
  placeholderText?: string;
}) {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }

    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const baseUrl = /^(?:https?:|data:|blob:)/i.test(src)
        ? ""
        : await getBaseUrl().catch(() => "");
      const requestUrl =
        baseUrl && !/^(?:https?:|data:|blob:)/i.test(src)
          ? `${baseUrl.replace(/\/+$/, "")}${src.startsWith("/") ? src : `/${src}`}`
          : src;
      const response = await fetch(requestUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error("Image unavailable");
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) {
    return <div style={placeholderStyle}>{placeholderText}</div>;
  }

  if (!resolvedSrc && failed) {
    return <div style={placeholderStyle}>Image unavailable</div>;
  }

  if (!resolvedSrc) {
    return <div style={placeholderStyle}>Loading image...</div>;
  }

  return <img src={resolvedSrc} alt={alt} style={style} />;
}

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const display = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  const valueText = text(value);
  return valueText || null;
};

const numeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatEstimate = (value: unknown): string | null => {
  const minutes = numeric(value);
  if (!minutes || minutes <= 0) return null;
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${remainder}m`;
};

const compactText = (value: unknown) => text(value);

const formatPreparationPart = (item: any): string => {
  if (typeof item === "string") return item.trim();
  const type = String(item?.catalogType || "PART").toUpperCase();
  const number = compactText(item?.partNumber || item?.kitNumber || item?.sku);
  const description = compactText(item?.description || item?.name);
  const reason = compactText(item?.reason);
  const source = compactText(item?.source || item?.sourceType);
  const title = [type, number].filter(Boolean).join(" ");
  const details = [description, reason].filter(Boolean).join(" — ");
  return [title, details, source ? `(source: ${source})` : ""].filter(Boolean).join(" ");
};

const capturePlaceholder = (capture: any) => {
  const units = display(capture?.units);
  return units ? `Enter value (${units})` : "Enter measured value";
};

const captureHint = (capture: any) => {
  const units = display(capture?.units);
  const low = numeric(capture?.expectedLow);
  const high = numeric(capture?.expectedHigh);
  const target = numeric(capture?.target);
  const tolerance = numeric(capture?.tolerance);
  const method = display(capture?.method);
  const notes = display(capture?.notes);
  const parts: string[] = [];
  if (low !== null && high !== null) parts.push(`Range ${low}${units ? ` ${units}` : ""} - ${high}${units ? ` ${units}` : ""}`);
  else if (low !== null) parts.push(`Minimum ${low}${units ? ` ${units}` : ""}`);
  else if (high !== null) parts.push(`Maximum ${high}${units ? ` ${units}` : ""}`);
  else if (target !== null && tolerance !== null && tolerance > 0) {
    parts.push(`Target ${target}${units ? ` ${units}` : ""} +/- ${tolerance}${units ? ` ${units}` : ""}`);
  }
  if (method) parts.push(`Method: ${method}`);
  if (notes) parts.push(notes);
  return parts.join(" | ");
};

const composeChecklistItemText = (item: any): string => {
  const label = text(item?.label || item?.description);
  const spec = text(item?.spec);
  if (label && spec) return `${label}: ${spec}`;
  return label || spec || "";
};

const normalizeWhitespace = (value: string): string =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const findNonOverlappingPhraseRange = (
  source: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
): { start: number; end: number } | null => {
  if (!source || !phrase) return null;
  const lowerSource = source.toLowerCase();
  const lowerPhrase = phrase.toLowerCase();
  let index = lowerSource.indexOf(lowerPhrase);
  while (index !== -1) {
    const candidate = { start: index, end: index + phrase.length };
    const overlaps = occupied.some(
      range => !(candidate.end <= range.start || candidate.start >= range.end)
    );
    if (!overlaps) return candidate;
    index = lowerSource.indexOf(lowerPhrase, index + 1);
  }
  return null;
};

const resolveItemReferenceImages = (step: any, item: any, itemLookupKey: string) => {
  const stepMeta = step?.metadata && typeof step.metadata === "object" ? step.metadata : {};
  const stepImages = Array.isArray(stepMeta.referenceImages) ? stepMeta.referenceImages : [];
  const stepBlocks = Array.isArray(stepMeta.referenceImageBlocks) ? stepMeta.referenceImageBlocks : [];
  if (stepImages.length === 0 || stepBlocks.length === 0) {
    return { block: null, images: [] as any[] };
  }

  const itemText = composeChecklistItemText(item);
  if (!itemText) return { block: null, images: [] as any[] };

  const matchingBlock = stepBlocks.find((block: any) => {
    const blockItemId = String(block?.itemId || "").trim();
    if (!!blockItemId && !!itemLookupKey && blockItemId === String(itemLookupKey).trim()) return true;
    return normalizeWhitespace(block?.text || "") === itemText;
  });
  if (!matchingBlock) {
    return { block: null, images: [] as any[] };
  }

  const imageMap = new Map(stepImages.map((image: any) => [String(image?.id), image]));
  const images = Array.from(
    new Set((Array.isArray(matchingBlock?.imageIds) ? matchingBlock.imageIds : []).map((imageId: any) => String(imageId)))
  )
    .map(imageId => imageMap.get(imageId))
    .filter(Boolean);
  return { block: matchingBlock, images };
};

export const WorkflowProtocolPreview: React.FC<WorkflowProtocolPreviewProps> = ({
  workflowJson,
  emptyText = "No workflow steps are available.",
  onFeedbackRequest
}) => {
  const [previewImages, setPreviewImages] = React.useState<any[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = React.useState(0);
  const [previewZoom, setPreviewZoom] = React.useState(1);
  const workflow = normalizeWorkflowJson(workflowJson);
  const steps = Array.isArray(workflow.steps) ? workflow.steps : [];
  const estimate = formatEstimate(workflow.estimatedDurationMinutes || workflow.estimatedTimeMinutes);
  const preparation = workflow?.preparation && typeof workflow.preparation === "object" ? workflow.preparation : {};
  const requiredParts = Array.isArray(preparation.requiredParts)
    ? preparation.requiredParts
    : Array.isArray(preparation.parts)
      ? preparation.parts
      : [];
  const activePreviewImage =
    previewImages.length > 0 ? previewImages[Math.min(previewImageIndex, previewImages.length - 1)] : null;

  const openPreviewImages = React.useCallback((images: any[], imageId?: string) => {
    if (!Array.isArray(images) || images.length === 0) return;
    const nextIndex = imageId ? Math.max(0, images.findIndex(image => image?.id === imageId)) : 0;
    setPreviewImages(images);
    setPreviewImageIndex(nextIndex >= 0 ? nextIndex : 0);
    setPreviewZoom(1);
  }, []);

  const closePreviewImages = React.useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(1);
  }, []);

    const renderWorkflowImageTextLinks = React.useCallback(
      (item: any, linkedImages: { block: any; images: any[] }) => {
      const matchingBlock = linkedImages.block;
      if (!matchingBlock || !Array.isArray(matchingBlock.anchors) || matchingBlock.anchors.length === 0) {
        return null;
      }

      const itemText = composeChecklistItemText(item);
      if (!itemText) return null;

      const imageMap = new Map(linkedImages.images.map((image: any) => [String(image?.id || ""), image]));
      type AnchorMatch = {
        anchorIndex: number;
        anchorText: string;
        start: number;
        end: number;
        images: any[];
      };
      const occupied: Array<{ start: number; end: number }> = [];
      const anchorMatches = matchingBlock.anchors
        .map((anchor: any, anchorIndex: number) => {
          const anchorText = normalizeWhitespace(anchor?.text || "");
          if (!anchorText) return null;
          const range = findNonOverlappingPhraseRange(itemText, anchorText, occupied);
          if (!range) return null;
          const imagesForAnchor = (Array.isArray(anchor?.imageIds) ? anchor.imageIds : [])
            .map((imageId: any) => imageMap.get(String(imageId)))
            .filter(Boolean);
          occupied.push(range);
          return {
            anchorIndex,
            anchorText,
            start: range.start,
            end: range.end,
            images: imagesForAnchor
          } as AnchorMatch;
        })
        .filter((anchor: AnchorMatch | null): anchor is AnchorMatch => anchor !== null)
        .sort((left: any, right: any) => left.start - right.start);
      if (anchorMatches.length === 0) return null;

      const parts: React.ReactNode[] = [];
      let cursor = 0;

      anchorMatches.forEach((anchorMatch: any) => {
        if (anchorMatch.start > cursor) {
          parts.push(
            <React.Fragment key={`text-${anchorMatch.anchorIndex}-${cursor}`}>
              {itemText.slice(cursor, anchorMatch.start)}
            </React.Fragment>
          );
        }

        if (anchorMatch.images.length > 0) {
          parts.push(
            <button
              key={`anchor-${anchorMatch.anchorIndex}-${anchorMatch.start}`}
              type="button"
              onClick={() => openPreviewImages(anchorMatch.images, anchorMatch.images[0]?.id)}
              style={styles.inlineAnchorButton}
            >
              {itemText.slice(anchorMatch.start, anchorMatch.end)}
            </button>
          );
        } else {
          parts.push(
            <React.Fragment key={`anchor-text-${anchorMatch.anchorIndex}-${anchorMatch.start}`}>
              {itemText.slice(anchorMatch.start, anchorMatch.end)}
            </React.Fragment>
          );
        }

        cursor = anchorMatch.end;
      });

      if (parts.length === 0) return null;
      if (cursor < itemText.length) {
        parts.push(<React.Fragment key={`text-tail-${cursor}`}>{itemText.slice(cursor)}</React.Fragment>);
      }
      return <span>{parts}</span>;
    },
    [openPreviewImages]
  );

  if (steps.length === 0) {
    return <div style={styles.empty}>{emptyText}</div>;
  }

  return (
    <div style={styles.shell}>
      <div style={styles.protocolMeta}>
        <span>{steps.length} step{steps.length === 1 ? "" : "s"}</span>
        {requiredParts.length > 0 ? <span>{requiredParts.length} required part(s)/kit(s)</span> : null}
        {estimate ? <span>Estimate: {estimate}</span> : null}
      </div>
      {requiredParts.length > 0 ? (
        <section style={styles.preparationCard}>
          <div style={styles.preparationTitle}>Required parts / kits</div>
          <ul style={styles.preparationList}>
            {requiredParts.map((item: any, index: number) => (
              <li key={item?.catalogId || item?.partNumber || item?.kitNumber || item?.sku || index}>
                {formatPreparationPart(item)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div style={styles.steps}>
        {steps.map((step: any, index: number) => {
          const checklist = getWorkflowStepChecklist(step);
          return (
            <section key={step?.stepKey || step?.id || index} style={styles.stepCard}>
              <div style={styles.stepHeading}>
                <span style={styles.stepIndex}>{index + 1}</span>
                <div style={styles.stepHeadingCopy}>
                  <div style={styles.stepTitle}>{step?.label || step?.title || `Step ${index + 1}`}</div>
                </div>
                {onFeedbackRequest ? (
                  <button
                    type="button"
                    onClick={() =>
                      onFeedbackRequest({
                        scope: "SECTION",
                        title: step?.label || step?.title || `Section ${index + 1}`,
                        stepIndex: index,
                        step
                      })
                    }
                    style={{
                      border: "1px solid #E1CDA8",
                      backgroundColor: "#FFFDF9",
                      borderRadius: "999px",
                      color: "#7A4A08",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      padding: "0.24rem 0.6rem",
                      cursor: "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Flag section
                  </button>
                ) : null}
              </div>
              {checklist.length > 0 ? (
                  <ul style={styles.checklist}>
                        {checklist.map((item: any, itemIndex: number) => {
                        const itemLookupKey = `${step?.stepKey || index}:${item?.id || itemIndex + 1}`;
                        const capture = item?.capture && typeof item.capture === "object" ? item.capture : null;
                        const hint = capture ? captureHint(capture) : "";
                        const linkedImages = resolveItemReferenceImages(step, item, itemLookupKey);
                        const linkedText = renderWorkflowImageTextLinks(item, linkedImages);
                    return (
                          <li key={`${itemLookupKey}`} style={styles.checklistItem}>
                        <div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: "0.55rem", flexWrap: "wrap" }}>
                          {linkedText ? (
                            linkedText
                          ) : (
                            <>
                              <span style={styles.checkLabel}>{item?.label || item?.description}</span>
                              {item?.spec ? <span style={styles.checkSpec}> | {item.spec}</span> : null}
                            </>
                          )}
                          {onFeedbackRequest ? (
                            <button
                              type="button"
                              onClick={() =>
                                onFeedbackRequest({
                                  scope: "ITEM",
                                  title: item?.label || `Checklist item ${itemIndex + 1}`,
                                  stepIndex: index,
                                  itemIndex,
                                  step,
                                  item
                                })
                              }
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#9A5A06",
                                fontSize: "0.76rem",
                                fontWeight: 700,
                                marginLeft: "0.55rem",
                                padding: 0,
                                textDecoration: "underline",
                                textUnderlineOffset: "0.12em",
                                cursor: "pointer"
                              }}
                            >
                              Flag
                            </button>
                          ) : null}
                          </div>
                        </div>
                        {capture ? (
                          <div style={styles.captureBlock}>
                            <div style={styles.captureHeader}>Measured value</div>
                            {hint ? <div style={styles.captureHint}>{hint}</div> : null}
                            <input style={styles.captureInput} disabled placeholder={capturePlaceholder(capture)} />
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div style={styles.emptyInline}>No checklist entries captured for this step.</div>
              )}
            </section>
          );
        })}
      </div>
      {activePreviewImage ? (
        <div style={styles.previewBackdrop}>
          <div style={styles.previewCard} onClick={event => event.stopPropagation()}>
            <div style={styles.previewHeader}>
              <div style={styles.previewHeaderCopy}>
                <div style={styles.previewTitle}>{activePreviewImage?.label || "Linked image"}</div>
                <div style={styles.previewCount}>
                  {previewImageIndex + 1} / {previewImages.length}
                </div>
              </div>
              <div style={styles.previewHeaderActions}>
                <button type="button" style={styles.previewCloseButton} onClick={closePreviewImages}>
                  X
                </button>
              </div>
            </div>
            <div style={styles.previewToolbar}>
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => {
                  setPreviewImageIndex(current => (current > 0 ? current - 1 : current));
                  setPreviewZoom(1);
                }}
                disabled={previewImageIndex <= 0}
              >
                Previous
              </button>
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => {
                  setPreviewImageIndex(current => (current < previewImages.length - 1 ? current + 1 : current));
                  setPreviewZoom(1);
                }}
                disabled={previewImageIndex >= previewImages.length - 1}
              >
                Next
              </button>
              <button
                type="button"
                style={styles.previewZoomButton}
                onClick={() => setPreviewZoom(current => Math.max(0.5, Number((current - 0.25).toFixed(2))))}
              >
                -
              </button>
              <input
                type="range"
                min={1}
                max={4}
                step={0.1}
                value={previewZoom}
                onChange={event => setPreviewZoom(Number(event.target.value))}
                style={styles.previewZoomRange}
              />
              <button
                type="button"
                style={styles.previewZoomButton}
                onClick={() => setPreviewZoom(current => Math.min(4, Number((current + 0.25).toFixed(2))))}
              >
                +
              </button>
              <button
                type="button"
                style={styles.previewZoomResetButton}
                onClick={() => setPreviewZoom(1)}
              >
                100%
              </button>
              <span style={styles.previewZoomLabel}>{Math.round(previewZoom * 100)}%</span>
            </div>
            {previewImages.length > 1 ? (
              <div style={styles.previewThumbStrip}>
                {previewImages.map((image, index) => (
                  <button
                    key={String(image?.id || index)}
                    type="button"
                    onClick={() => {
                      setPreviewImageIndex(index);
                      setPreviewZoom(1);
                    }}
                    style={{
                      ...styles.previewThumbButton,
                      ...(index === previewImageIndex ? styles.previewThumbButtonActive : {})
                    }}
                  >
                    <AuthenticatedReferenceImage
                      src={image?.fileUrl || null}
                      alt={image?.label || `Linked image ${index + 1}`}
                      style={styles.previewThumbImage}
                      placeholderStyle={styles.previewThumbPlaceholder}
                    />
                  </button>
                ))}
              </div>
            ) : null}
            <div style={styles.previewViewportOuter}>
              <div style={styles.previewViewportInner}>
                <AuthenticatedReferenceImage
                  src={activePreviewImage?.fileUrl || null}
                  alt={activePreviewImage?.label || "Linked image"}
                  style={{
                    ...styles.previewImage,
                    maxWidth: previewZoom <= 1 ? "100%" : `${previewZoom * 100}%`,
                    maxHeight: previewZoom <= 1 ? "56vh" : "none"
                  }}
                  placeholderStyle={styles.previewImagePlaceholder}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: "grid",
    gap: "0.85rem"
  },
  protocolMeta: {
    display: "flex",
    gap: "0.8rem",
    flexWrap: "wrap",
    color: "#52615f",
    fontSize: "0.92rem"
  },
  preparationCard: {
    border: "1px solid #DDE5EA",
    borderRadius: 8,
    background: "#FFFFFF",
    padding: "0.75rem 0.85rem",
    display: "grid",
    gap: "0.55rem"
  },
  preparationTitle: {
    color: "#123F43",
    fontWeight: 800,
    fontSize: "0.92rem"
  },
  preparationList: {
    margin: 0,
    paddingLeft: "1.15rem",
    display: "grid",
    gap: "0.35rem",
    color: "#1f2937"
  },
  steps: {
    display: "grid",
    gap: "0.85rem"
  },
  stepCard: {
    border: "1px solid #DDE5EA",
    borderRadius: 8,
    background: "#FFFFFF",
    padding: "0.95rem",
    display: "grid",
    gap: "0.75rem"
  },
  stepHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.8rem"
  },
  stepHeadingCopy: {
    minWidth: 0,
    flex: 1
  },
  stepIndex: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "#DCEBFF",
    color: "#123F43",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    flex: "0 0 auto"
  },
  stepTitle: {
    color: "#123F43",
    fontWeight: 800,
    fontSize: "1rem",
    lineHeight: 1.3
  },
  checklist: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: "0.6rem"
  },
  checklistItem: {
    color: "#1f2937",
    lineHeight: 1.45,
    display: "grid",
    gap: "0.35rem"
  },
  inlineAnchorButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    color: "#1F5F63",
    textDecoration: "underline",
    textUnderlineOffset: "0.12em",
    cursor: "pointer",
    font: "inherit"
  },
  checkLabel: {
    fontWeight: 700
  },
  checkSpec: {
    color: "#475569"
  },
  captureBlock: {
    display: "grid",
    gap: "0.32rem",
    maxWidth: 340
  },
  captureHeader: {
    color: "#123F43",
    fontSize: "0.78rem",
    fontWeight: 800
  },
  captureHint: {
    color: "#52615f",
    fontSize: "0.82rem"
  },
  captureInput: {
    border: "1px solid #BFD8D1",
    borderRadius: 8,
    background: "#F8FBFA",
    padding: "0.62rem 0.75rem",
    color: "#52615f",
    font: "inherit"
  },
  empty: {
    border: "1px dashed #DDD4C8",
    borderRadius: 8,
    color: "#52615f",
    background: "#FFFDF9",
    padding: "1rem"
  },
  emptyInline: {
    color: "#52615f",
    fontSize: "0.9rem"
  },
  previewBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.2rem",
    zIndex: 1100
  },
  previewCard: {
    width: "min(1120px, 100%)",
    maxHeight: "92vh",
    overflow: "hidden",
    borderRadius: 16,
    background: "#FFFFFF",
    boxShadow: "0 28px 70px rgba(15, 23, 42, 0.24)",
    display: "grid",
    gridTemplateRows: "auto auto auto minmax(0, 1fr)"
  },
  previewHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1rem 1rem 0.75rem 1rem"
  },
  previewHeaderCopy: {
    display: "grid",
    gap: "0.18rem"
  },
  previewHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.55rem"
  },
  previewCount: {
    color: "#52615f",
    fontSize: "0.8rem",
    fontWeight: 700
  },
  previewTitle: {
    color: "#123F43",
    fontWeight: 800,
    fontSize: "1rem"
  },
  previewCloseButton: {
    border: "1px solid #DDD4C8",
    background: "#FFFDF9",
    color: "#123F43",
    borderRadius: "0.75rem",
    width: 38,
    height: 38,
    cursor: "pointer",
    fontWeight: 700
  },
  previewToolbar: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0 1rem 0.75rem 1rem",
    flexWrap: "wrap"
  },
  previewToolbarButton: {
    border: "1px solid #DDD4C8",
    backgroundColor: "#FFFDF9",
    borderRadius: "0.55rem",
    color: "#123F43",
    padding: "0.4rem 0.75rem",
    cursor: "pointer"
  },
  previewZoomButton: {
    border: "1px solid #DDD4C8",
    backgroundColor: "#FFFDF9",
    borderRadius: "0.55rem",
    color: "#123F43",
    padding: "0.4rem 0.7rem",
    cursor: "pointer"
  },
  previewZoomRange: {
    width: "220px"
  },
  previewZoomResetButton: {
    border: "1px solid #DDD4C8",
    backgroundColor: "#FFFDF9",
    borderRadius: "0.55rem",
    color: "#123F43",
    padding: "0.4rem 0.7rem",
    cursor: "pointer",
    fontWeight: 600
  },
  previewZoomLabel: {
    color: "#64716F",
    fontSize: "0.85rem"
  },
  previewThumbStrip: {
    display: "flex",
    gap: "0.6rem",
    padding: "0 1rem 0.75rem 1rem",
    overflowX: "auto"
  },
  previewThumbButton: {
    border: "1px solid #DDD4C8",
    backgroundColor: "#FFFDF9",
    borderRadius: "0.7rem",
    padding: "0.2rem",
    width: "84px",
    height: "84px",
    overflow: "hidden",
    cursor: "pointer",
    flex: "0 0 auto"
  },
  previewThumbButtonActive: {
    border: "2px solid #1F5F63"
  },
  previewThumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
  },
  previewThumbPlaceholder: {
    width: "100%",
    height: "100%",
    backgroundColor: "#F5F2EC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    color: "#64716F"
  },
  previewViewportOuter: {
    minHeight: 0,
    overflow: "auto",
    padding: "0 1rem 1rem 1rem"
  },
  previewViewportInner: {
    minHeight: "52vh",
    maxHeight: "60vh",
    overflow: "auto",
    borderRadius: "0.75rem",
    backgroundColor: "#F7F3EC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem"
  },
  previewImage: {
    display: "block",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    borderRadius: 8
  },
  previewImagePlaceholder: {
    width: "100%",
    minHeight: 260,
    borderRadius: 8,
    background: "#F5F2EC",
    color: "#64716F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem"
  }
};

export default WorkflowProtocolPreview;
